import { useState, type ReactNode } from 'react';

/**
 * Shared admin-table tools: a search box + a sortable-column hook. Every
 * admin list (Users & Roles, Stores, Districts, Notification rules, sent
 * log) uses these so the tables behave identically. All filtering/sorting
 * is client-side — admin lists are small, fetched-once datasets.
 */

/** Case-insensitive substring match across any of the row's search fields. */
export function textMatch(
  query: string,
  ...values: Array<string | number | null | undefined>
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return values.some((v) => String(v ?? '').toLowerCase().includes(q));
}

export function SearchBox({
  value,
  onChange,
  placeholder = 'Search…',
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  return (
    <span className="search-box">
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </span>
  );
}

export type SortDir = 'asc' | 'desc';
type SortValue = string | number | boolean | null | undefined;

/**
 * Sortable columns for one table. `getters` maps a column key to the value
 * to sort that column by; `th(label, key)` renders a clickable header cell
 * (or a plain one when `key` is omitted, e.g. the actions column) and
 * `apply(rows)` returns the rows in the current order. With no column
 * clicked yet, `apply` returns the rows untouched (the page's natural
 * order); clicking toggles asc → desc → asc.
 */
export function useSortableTable<Row>(
  getters: Record<string, (row: Row) => SortValue>,
  initial?: { key: string; dir: SortDir },
) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(
    initial ?? null,
  );

  const toggle = (key: string) =>
    setSort((s) =>
      s?.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );

  const th = (label: ReactNode, key?: string, className?: string) => {
    if (!key) return <th className="row-actions">{label}</th>;
    const active = sort?.key === key;
    return (
      <th
        key={key}
        className={className}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      >
        <button type="button" className="th-sort" onClick={() => toggle(key)}>
          {label}
          <span className="th-sort-arrow" aria-hidden>
            {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
          </span>
        </button>
      </th>
    );
  };

  const apply = (rows: readonly Row[]): Row[] => {
    if (!sort) return [...rows];
    const get = getters[sort.key];
    if (!get) return [...rows];
    const flip = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      // Blanks sort last regardless of direction.
      const aBlank = va === null || va === undefined || va === '';
      const bBlank = vb === null || vb === undefined || vb === '';
      if (aBlank || bBlank) return aBlank === bBlank ? 0 : aBlank ? 1 : -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * flip;
      }
      if (typeof va === 'boolean' && typeof vb === 'boolean') {
        return (Number(va) - Number(vb)) * flip;
      }
      return (
        String(va).localeCompare(String(vb), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * flip
      );
    });
  };

  return { th, apply };
}
